import { useState } from "react";
import axios from "axios";
import Layout from "../Common/Layout";

export default function QcDateRangeReport() {
  const token = sessionStorage.getItem("token");

  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        "https://irc.zentelex.com//api/reports/qc/date-range",
        {
          params: { fromDate, toDate },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setRecords(res.data);
    } catch (error) {
      console.error(error);
      alert("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter((r) => {
    const text = search.toLowerCase();

    return (
      r.clientName?.toLowerCase().includes(text) ||
      r.commodityName?.toLowerCase().includes(text) ||
      r.varietyName?.toLowerCase().includes(text) ||
      r.warehouseName?.toLowerCase().includes(text) ||
      r.samplingDate?.toLowerCase().includes(text)
    );
  });

  const totalBags = filteredRecords.reduce(
    (sum, r) => sum + Number(r.noOfBags || 0),
    0,
  );

  const totalQty = filteredRecords.reduce(
    (sum, r) => sum + Number(r.quantity || 0),
    0,
  );

  const openModal = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="card shadow-lg border-0 mb-4 bg-primary text-white">
        <div className="card-body">
          <h4 className="fw-bold mb-1">Quality QC Report</h4>
          <p className="mb-0 opacity-75">Sampling Date Range Report</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow-sm border-0 mb-4 bg-light">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-semibold text-primary">
                From Date
              </label>
              <input
                type="date"
                className="form-control border-primary"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold text-primary">
                To Date
              </label>
              <input
                type="date"
                className="form-control border-primary"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <button
                className="btn btn-primary w-100 fw-semibold shadow-sm"
                onClick={fetchReport}
                disabled={loading}
              >
                {loading ? "Loading..." : "Generate Report"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <input
            type="text"
            className="form-control border-primary"
            placeholder="🔍 Search Client / Commodity / Variety / Warehouse..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-primary text-center">
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Commodity</th>
                  <th>Variety</th>
                  <th>No. of Bags</th>
                  <th>Quantity (MT)</th>
                  <th>Warehouse</th>
                  <th>Quality</th>
                </tr>
              </thead>

              <tbody>
                {!loading && filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No records found
                    </td>
                  </tr>
                )}

                {filteredRecords.map((r) => (
                  <tr key={r.id}>
                    <td className="text-center">{r.samplingDate}</td>
                    <td>{r.clientName}</td>
                    <td>{r.commodityName}</td>
                    <td>{r.varietyName}</td>
                    <td className="text-end fw-semibold text-primary">
                      {r.noOfBags}
                    </td>
                    <td className="text-end fw-semibold text-success">
                      {r.quantity}
                    </td>
                    <td>{r.warehouseName}</td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-primary fw-semibold"
                        onClick={() => openModal(r)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

              {filteredRecords.length > 0 && (
                <tfoot className="table-dark fw-bold">
                  <tr>
                    <td colSpan="4" className="text-end">
                      TOTAL
                    </td>
                    <td className="text-end">{totalBags}</td>
                    <td className="text-end">{totalQty.toFixed(2)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {showModal && selectedRecord && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop show" onClick={closeModal}></div>

          {/* Modal */}
          <div className="modal show" style={{ display: "block" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title fw-bold">QC Details</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Basic Info */}
                  <h6 className="fw-bold text-primary mb-3">
                    Basic Information
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <small className="text-muted">Client</small>
                      <div className="fw-semibold">
                        {selectedRecord.clientName}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">Commodity</small>
                      <div className="fw-semibold">
                        {selectedRecord.commodityName}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">Variety</small>
                      <div className="fw-semibold">
                        {selectedRecord.varietyName}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">Warehouse</small>
                      <div className="fw-semibold">
                        {selectedRecord.warehouseName}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">No. of Bags</small>
                      <div className="fw-semibold">
                        {selectedRecord.noOfBags}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">Quantity (MT)</small>
                      <div className="fw-semibold">
                        {selectedRecord.quantity}
                      </div>
                    </div>
                  </div>

                  {/* Quality Details */}
                  <h6 className="fw-bold text-primary mb-3">
                    Quality Parameters
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <small className="text-muted">Odour</small>
                      <div className="fw-semibold">{selectedRecord.odour}</div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Moisture Content</small>
                      <div className="fw-semibold">
                        {selectedRecord.moistureContent}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Foreign Matter</small>
                      <div className="fw-semibold">
                        {selectedRecord.foreignMatter}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Damaged Grains</small>
                      <div className="fw-semibold">
                        {selectedRecord.damagedGrains}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Weevilled Grain</small>
                      <div className="fw-semibold">
                        {selectedRecord.weevilledGrain}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Immature Grain</small>
                      <div className="fw-semibold">
                        {selectedRecord.immatureGrain}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Broken Grain</small>
                      <div className="fw-semibold">
                        {selectedRecord.brokenGrain}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Other Food Grain</small>
                      <div className="fw-semibold">
                        {selectedRecord.otherFoodGrain}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Live Insect Presence</small>
                      <div
                        className={`fw-semibold ${
                          selectedRecord.liveInsectPresence === "No"
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        {selectedRecord.liveInsectPresence}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">Live Insect Status</small>
                      <div className="fw-semibold">
                        {selectedRecord.liveInsectStatus}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted">QA Analyst</small>
                      <div className="fw-semibold">
                        {selectedRecord.qaAnalystName}
                      </div>
                    </div>

                    <div className="col-12">
                      <small className="text-muted">Remarks</small>
                      <div className="fw-semibold">
                        {selectedRecord.remarks}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
